/**
 * Feuille d'actions du mode course — EF-NAV-006, EF-MOD-003.
 *
 * Remplace le bouton « Revenir au roadbook » : elle rassemble le retour au
 * roadbook et les deux commandes de position, qui referment des références
 * locales à `RaceScreen` (projecteur, position courante) et ne peuvent donc
 * pas transiter par la feuille d'actions globale de `App.tsx`.
 */

import type { JSX } from 'preact';
import { Glyph, type GlyphName } from '../icons';
import { formatArrival, NOT_AVAILABLE } from '../format';
import { ACTIONS, SCREENS, SHEETS } from '../labels';
import { closeSheet, raceState, screen } from '../state';
import { Sheet } from './Sheet';

export interface RaceActionsSheetProps {
  readonly hasPosition: boolean;
  readonly onCorrectPosition: () => void;
  readonly onRecompute: () => void;
}

export function RaceActionsSheet(props: RaceActionsSheetProps): JSX.Element {
  const lastComputed = raceState.value.lastComputedAtLocal;

  return (
    <Sheet title={SHEETS.raceActions} onClose={closeSheet}>
      <Entry
        glyph="retour"
        label={SCREENS.roadbook}
        disabledReason={null}
        onSelect={() => {
          closeSheet();
          screen.value = 'roadbook';
        }}
      />
      <Entry
        glyph="navigation"
        label={ACTIONS.correctPosition}
        disabledReason={null}
        onSelect={() => {
          closeSheet();
          props.onCorrectPosition();
        }}
      />
      <Entry
        glyph="calculer"
        label={ACTIONS.recomputeNow}
        hint={`Dernier calcul : ${lastComputed === null ? NOT_AVAILABLE : formatArrival(lastComputed, null)}`}
        disabledReason={props.hasPosition ? null : 'Aucune position actuelle.'}
        onSelect={() => {
          closeSheet();
          props.onRecompute();
        }}
      />
    </Sheet>
  );
}

function Entry(props: {
  readonly glyph: GlyphName;
  readonly label: string;
  readonly hint?: string;
  readonly disabledReason: string | null;
  readonly onSelect: () => void;
}): JSX.Element {
  const disabled = props.disabledReason !== null;
  return (
    <button type="button" class="sheet__item" disabled={disabled} onClick={props.onSelect}>
      <Glyph name={props.glyph} label="" size={26} decorative />
      <span class="sheet__item-text">
        <span>{props.label}</span>
        {props.disabledReason !== null ? (
          <span class="sheet__item-hint">{props.disabledReason}</span>
        ) : props.hint !== undefined ? (
          <span class="sheet__item-hint">{props.hint}</span>
        ) : null}
      </span>
      {disabled ? null : <Glyph name="suivant" label="" size={22} decorative />}
    </button>
  );
}
